import React, { useContext, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Button, CircularProgress, Typography } from "@mui/material"
import PrintIcon from "@mui/icons-material/Print"
import VisibilityIcon from "@mui/icons-material/Visibility"
import CvSkillDto from "../../api/models/cvskill"
import cvskillService from "../../api/services/cvskillService"
import UserContext from "../../contexts/user"
import userService from "../../api/services/userService"
import "./cv-skill.scss"

/**
 * Composant de visualisation CV Skill pour les utilisateurs
 * - Lecture seule (aucune modification possible)
 * - Fonction d'impression
 * - Accessible uniquement aux utilisateurs connectés
 */
function CvSkillUserView() {
	const { user: currentUser } = useContext(UserContext)
	const [cvSkill, setCvSkill] = useState<CvSkillDto | null>(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState<string | null>(null)
	const [userId, setUserId] = useState<number | null>(null)
	const navigate = useNavigate()

	// ✅ Effet pour récupérer l'ID utilisateur
	useEffect(() => {
		const getUserId = async () => {
			console.log(
				"[CvSkillUserView] getUserId - currentUser:",
				currentUser,
			)

			// Attendre que le contexte soit chargé
			if (currentUser === undefined) {
				console.log(
					"[CvSkillUserView] Contexte utilisateur en cours de chargement",
				)
				return
			}

			// Vérifier si l'utilisateur est connecté
			if (currentUser === null) {
				console.log("[CvSkillUserView] Utilisateur non connecté")
				setError("Vous devez être connecté pour accéder à cette page.")
				setLoading(false)
				return
			}

			// ✅ Si currentUser.id existe déjà
			if (currentUser.id) {
				console.log(
					"[CvSkillUserView] ID utilisateur trouvé dans le contexte:",
					currentUser.id,
				)
				setUserId(Number(currentUser.id))
				return
			}

			// ✅ Si currentUser.id n'existe pas, appeler getSelf() pour le récupérer
			console.log("[CvSkillUserView] ID manquant, appel à getSelf()")
			try {
				const fullUser = await userService.getSelf()
				console.log(
					"[CvSkillUserView] Utilisateur complet récupéré:",
					fullUser,
				)

				if (fullUser && fullUser.id) {
					console.log(
						"[CvSkillUserView] ID utilisateur récupéré:",
						fullUser.id,
					)
					setUserId(Number(fullUser.id))
				} else {
					console.error(
						"[CvSkillUserView] getSelf() n'a pas retourné d'ID",
					)
					setError(
						"Erreur : impossible de récupérer votre identifiant. Veuillez vous reconnecter.",
					)
					setLoading(false)
				}
			} catch (err) {
				console.error(
					"[CvSkillUserView] Erreur lors de getSelf():",
					err,
				)
				setError(
					"Erreur lors de la récupération de vos informations. Veuillez vous reconnecter.",
				)
				setLoading(false)
			}
		}

		getUserId()
	}, [currentUser])

	// ✅ Effet pour récupérer le CV Skill une fois qu'on a l'ID
	useEffect(() => {
		const fetchUserCvSkill = async () => {
			// Attendre d'avoir un userId
			if (userId === null) {
				console.log("[CvSkillUserView] En attente de l'ID utilisateur")
				return
			}

			try {
				setLoading(true)
				console.log(
					`[CvSkillUserView] Récupération du CV Skill pour l'utilisateur ID: ${userId}`,
				)

				// ✅ Utiliser l'endpoint /user/{userId} qui est sécurisé et autorisé
				const userCvSkill =
					await cvskillService.getCvSkillsByUserId(userId)

				console.log("[CvSkillUserView] Réponse de l'API:", userCvSkill)

				if (
					!userCvSkill ||
					(Array.isArray(userCvSkill) && userCvSkill.length === 0)
				) {
					console.log("[CvSkillUserView] Aucun CV Skill trouvé")
					setCvSkill(null)
					setError(
						"Vous n'avez pas encore de CV Skill. Veuillez contacter l'administration pour en créer un.",
					)
				} else {
					// Si c'est un tableau, prendre le premier élément
					const cvSkillData = Array.isArray(userCvSkill)
						? userCvSkill[0]
						: userCvSkill

					console.log(
						"[CvSkillUserView] CV Skill trouvé:",
						cvSkillData,
					)

					// ✅ D'ABORD mettre à jour cvSkill
					setCvSkill(cvSkillData)
					// ✅ ENSUITE réinitialiser l'erreur
					setError(null)

					// ✅ Stocker l'ID du CV Skill pour une utilisation future
					if (cvSkillData.id) {
						localStorage.setItem(
							"selectedCvSkillId",
							String(cvSkillData.id),
						)
						localStorage.setItem("selectedUserId", String(userId))
					}
					console.log(
						"[CvSkillUserView] CV Skill récupéré avec succès:",
						cvSkillData,
					)
				}
			} catch (err: any) {
				console.error(
					"[CvSkillUserView] Erreur lors de la récupération du CV Skill:",
					err,
				)
				console.error("[CvSkillUserView] Détails de l'erreur:", {
					status: err.response?.status,
					data: err.response?.data,
					message: err.message,
				})

				setCvSkill(null)

				if (err.response?.status === 404) {
					setError(
						"Vous n'avez pas encore de CV Skill. Veuillez contacter l'administration pour en créer un.",
					)
				} else if (err.response?.status === 403) {
					setError(
						"Vous n'avez pas l'autorisation d'accéder à cette page.",
					)
				} else {
					setError(
						"Une erreur est survenue lors de la récupération de votre CV Skill.",
					)
				}
			} finally {
				setLoading(false)
			}
		}

		fetchUserCvSkill()
	}, [userId]) // ✅ Se déclenche quand userId change

	const handlePrint = () => {
		if (!cvSkill) return

		// Stocker l'ID du CV Skill pour la page de prévisualisation
		localStorage.setItem("selectedCvSkillId", String(cvSkill.id))

		// Rediriger vers la page de thème/prévisualisation en mode lecture seule
		navigate("/cvskill/preview", {
			state: {
				cvSkillId: cvSkill.id,
				userId,
				readOnly: true, // Mode lecture seule
			},
		})
	}

	const handleViewCv = () => {
		if (!cvSkill) return

		localStorage.setItem("selectedCvSkillId", String(cvSkill.id))

		// Rediriger vers la page de visualisation du CV
		navigate("/cvskill/cv", {
			state: {
				cvSkillId: cvSkill.id,
				userId,
				readOnly: true, // Mode lecture seule
			},
		})
	}

	// 🔍 LOGS DE DIAGNOSTIC
	console.log("[CvSkillUserView] État actuel du rendu:", {
		loading,
		hasError: !!error,
		errorMessage: error,
		hasCvSkill: !!cvSkill,
		cvSkillId: cvSkill?.id,
		hasUser: !!currentUser,
		userId,
	})

	// ✅ CHARGEMENT
	if (loading) {
		return (
			<div className="cv-skill-user-view">
				<div className="loading-container">
					<CircularProgress size={60} style={{ color: "#2daf8e" }} />
					<Typography
						variant="h6"
						style={{ marginTop: "20px", color: "#2daf8e" }}
					>
						Chargement de votre CV Skill...
					</Typography>
				</div>
			</div>
		)
	}

	// ✅ ERREUR ET PAS DE CV SKILL = Page d'accueil
	// 🚨 CONDITION STRICTE : uniquement si error existe ET cvSkill est null
	if (error && !cvSkill) {
		return (
			<div className="cv-skill-user-view">
				<style>{`
					@keyframes fadeInUp {
						from {
							opacity: 0;
							transform: translateY(30px);
						}
						to {
							opacity: 1;
							transform: translateY(0);
						}
					}
					
					@keyframes float {
						0%, 100% {
							transform: translateY(0px);
						}
						50% {
							transform: translateY(-20px);
						}
					}
					
					@keyframes pulse {
						0%, 100% {
							transform: scale(1);
						}
						50% {
							transform: scale(1.05);
						}
					}
					
					.cv-skill-user-view {
						width: 100%;
						min-height: 100vh;
						max-height: 100vh;
						overflow-y: auto;
						display: flex;
						justify-content: center;
						align-items: flex-start;
						padding: 20px 20px 100px 20px;
						box-sizing: border-box;
					}
					
					.welcome-container {
						max-width: 700px;
						width: 100%;
						margin: 0 auto;
						padding: 40px 20px;
						text-align: center;
						animation: fadeInUp 0.8s ease-out;
					}
					
					.welcome-icon {
						font-size: 100px;
						animation: float 3s ease-in-out infinite;
						margin-bottom: 30px;
					}
					
					.welcome-title {
						font-size: 32px;
						font-weight: 700;
						color: #2daf8e;
						margin-bottom: 15px;
					}
					
					.welcome-subtitle {
						font-size: 18px;
						color: #374151;
						font-weight: 600;
						margin-bottom: 30px;
					}
					
					.welcome-description {
						background: white;
						border-radius: 16px;
						padding: 25px;
						margin-bottom: 30px;
						box-shadow: 0 4px 20px rgba(45, 175, 142, 0.15);
						border: 2px solid #e5e7eb;
					}
					
					.welcome-description p {
						color: #6b7280;
						font-size: 16px;
						line-height: 1.8;
						margin: 0;
					}
					
					.contact-card {
						background: linear-gradient(135deg, #f0fdf9 0%, #e6f7f3 100%);
						border-radius: 16px;
						padding: 30px;
						margin-bottom: 30px;
						border: 2px solid #2daf8e;
						animation: pulse 2s ease-in-out infinite;
					}
					
					.contact-card h3 {
						color: #2daf8e;
						font-size: 22px;
						margin: 0 0 15px 0;
						font-weight: 700;
					}
					
					.contact-card p {
						color: #374151;
						font-size: 16px;
						margin: 10px 0;
						line-height: 1.6;
					}
					
					.email-link {
						display: inline-flex;
						align-items: center;
						gap: 10px;
						background: white;
						color: #2daf8e;
						padding: 12px 24px;
						border-radius: 12px;
						text-decoration: none;
						font-weight: 600;
						font-size: 16px;
						margin-top: 15px;
						transition: all 0.3s ease;
						box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
					}
					
					.email-link:hover {
						transform: translateY(-2px);
						box-shadow: 0 4px 12px rgba(45, 175, 142, 0.3);
						background: #2daf8e;
						color: white;
					}
					
					.back-button {
						margin-top: 20px;
						margin-bottom: 40px;
					}
					
					.loading-container {
						display: flex;
						flex-direction: column;
						align-items: center;
						justify-content: center;
						min-height: 400px;
						text-align: center;
					}
					
					/* Responsive */
					@media (max-width: 768px) {
						.welcome-container {
							padding: 30px 15px;
						}
						
						.welcome-icon {
							font-size: 80px;
						}
						
						.welcome-title {
							font-size: 26px;
						}
						
						.welcome-subtitle {
							font-size: 16px;
						}
						
						.welcome-description {
							padding: 20px;
						}
						
						.welcome-description p {
							font-size: 14px;
						}
						
						.contact-card {
							padding: 25px;
						}
						
						.contact-card h3 {
							font-size: 20px;
						}
						
						.contact-card p {
							font-size: 14px;
						}
						
						.email-link {
							font-size: 14px;
							padding: 10px 20px;
						}
					}
					
					@media (max-width: 480px) {
						.welcome-icon {
							font-size: 60px;
						}
						
						.welcome-title {
							font-size: 22px;
						}
						
						.welcome-subtitle {
							font-size: 14px;
						}
					}
				`}</style>

				<div className="welcome-container">
					<div className="welcome-icon">📄✨</div>

					<h1 className="welcome-title">Bienvenue sur CV Skill</h1>

					<h2 className="welcome-subtitle">
						Un nouveau concept développé par Entreprendre Ensemble
					</h2>

					<div className="welcome-description">
						<p>
							<strong>CV Skill</strong> est une solution innovante
							qui vous aide à créer un CV professionnel qui met en
							valeur vos <strong>compétences</strong>, votre{" "}
							<strong>personnalité</strong> et vos{" "}
							<strong>atouts uniques</strong>.
						</p>
					</div>

					<div className="contact-card">
						<h3>🚀 Vous souhaitez créer votre CV Skill ?</h3>
						<p>
							L'équipe <strong>JobExplorer</strong> d'Entreprendre
							Ensemble est là pour vous accompagner dans la
							création de votre CV professionnel.
						</p>
						<a
							href="mailto:jobexplorer@eedk.fr"
							className="email-link"
						>
							📧 jobexplorer@eedk.fr
						</a>
					</div>

					<div className="back-button">
						<Button
							variant="contained"
							onClick={() => navigate("/companies")}
							style={{
								background:
									"linear-gradient(135deg, #2daf8e 0%, #1e8a6f 100%)",
								color: "white",
								padding: "12px 32px",
								fontSize: "16px",
								fontWeight: 600,
								borderRadius: "12px",
								textTransform: "none",
								boxShadow: "0 4px 12px rgba(45, 175, 142, 0.3)",
							}}
						>
							Retour à l'accueil
						</Button>
					</div>
				</div>
			</div>
		)
	}

	// ✅ CV SKILL TROUVÉ = Page principale
	return (
		<div className="cv-skill-user-view">
			<style>{`
				.cv-skill-user-view {
					max-width: 1000px;
					margin: 40px auto;
					padding: 20px;
				}
				
				.loading-container,
				.error-container {
					display: flex;
					flex-direction: column;
					align-items: center;
					justify-content: center;
					min-height: 400px;
					text-align: center;
				}
				
				.cv-header {
					background: linear-gradient(135deg, #2daf8e 0%, #1e8a6f 100%);
					color: white;
					padding: 30px;
					border-radius: 16px;
					margin-bottom: 30px;
					box-shadow: 0 4px 12px rgba(45, 175, 142, 0.3);
				}
				
				.cv-header h2 {
					margin: 0 0 10px 0;
					font-size: 32px;
					font-weight: 700;
				}
				
				.cv-header p {
					margin: 0;
					font-size: 16px;
					opacity: 0.9;
				}
				
				.cv-info-card {
					background: white;
					border-radius: 12px;
					padding: 30px;
					margin-bottom: 20px;
					box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
				}
				
				.cv-info-row {
					display: flex;
					justify-content: space-between;
					align-items: center;
					margin-bottom: 15px;
					padding-bottom: 15px;
					border-bottom: 1px solid #e5e7eb;
				}
				
				.cv-info-row:last-child {
					border-bottom: none;
					margin-bottom: 0;
					padding-bottom: 0;
				}
				
				.cv-info-label {
					font-weight: 600;
					color: #374151;
					font-size: 14px;
				}
				
				.cv-info-value {
					color: #6b7280;
					font-size: 14px;
				}
				
				.cv-actions {
					display: flex;
					gap: 15px;
					justify-content: center;
					margin-top: 30px;
				}
				
				.action-btn {
					display: flex;
					align-items: center;
					gap: 10px;
					padding: 14px 28px;
					border-radius: 12px;
					font-size: 16px;
					font-weight: 600;
					text-transform: none;
					transition: all 0.3s ease;
					box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
				}
				
				.action-btn:hover {
					transform: translateY(-2px);
					box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
				}
				
				.view-btn {
					background: linear-gradient(135deg, #2daf8e 0%, #1e8a6f 100%);
					color: white;
				}
				
				.print-btn {
					background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
					color: white;
				}
				
				.info-section {
					background: #f9fafb;
					border-left: 4px solid #2daf8e;
					padding: 20px;
					border-radius: 8px;
					margin-top: 20px;
				}
				
				.info-section h4 {
					color: #2daf8e;
					margin: 0 0 10px 0;
					font-size: 18px;
				}
				
				.info-section p {
					color: #6b7280;
					margin: 0;
					line-height: 1.6;
				}
			`}</style>

			<div className="cv-header">
				<h2>📄 Mon CV Skill</h2>
				<p>Visualisez et imprimez votre CV professionnel</p>
			</div>

			{cvSkill && (
				<>
					<div className="cv-info-card">
						<div className="cv-info-row">
							<span className="cv-info-label">Nom complet</span>
							<span className="cv-info-value">
								{cvSkill.user?.firstName} {cvSkill.user?.name}
							</span>
						</div>
						<div className="cv-info-row">
							<span className="cv-info-label">Email</span>
							<span className="cv-info-value">
								{cvSkill.user?.email}
							</span>
						</div>
						<div className="cv-info-row">
							<span className="cv-info-label">
								Thème sélectionné
							</span>
							<span className="cv-info-value">
								{cvSkill.themeId
									? `Thème ${cvSkill.themeId}`
									: "Thème par défaut"}
							</span>
						</div>
						<div className="cv-info-row">
							<span className="cv-info-label">Avec photo</span>
							<span className="cv-info-value">
								{cvSkill.withPhoto ? "Oui ✅" : "Non ❌"}
							</span>
						</div>
					</div>

					<div className="info-section">
						<h4>ℹ️ Information importante</h4>
						<p>
							Votre CV Skill est en <strong>lecture seule</strong>
							. Vous pouvez le visualiser et l'imprimer, mais les
							modifications doivent être effectuées par
							l'administration. Si vous souhaitez modifier votre
							CV, veuillez contacter l'équipe d'Entreprendre
							Ensemble.
						</p>
					</div>

					<div className="cv-actions">
						<Button
							variant="contained"
							className="action-btn view-btn"
							startIcon={<VisibilityIcon />}
							onClick={handleViewCv}
						>
							Voir mon CV
						</Button>
						<Button
							variant="contained"
							className="action-btn print-btn"
							startIcon={<PrintIcon />}
							onClick={handlePrint}
						>
							Imprimer mon CV
						</Button>
					</div>
				</>
			)}
		</div>
	)
}

export default CvSkillUserView
